/**
 * Português e espanhol na mesma instalação.
 *
 * O idioma vem do endereço — `validador.allingressos.com.py` abre em
 * espanhol, `validador.allingressos.com.br` em português —, e o operador pode
 * trocar na tela; a escolha dele fica lembrada no aparelho.
 *
 * A chave de cada texto é o próprio texto em português. Assim as telas
 * continuam legíveis para quem trabalha nelas (o código da casa é em
 * português), e texto sem tradução aparece em português em vez de sumir ou
 * virar uma chave técnica na cara do operador. `scripts/conferir-idioma.mjs`
 * falha o `npm test` quando algum `txt(...)` não tem tradução — é o que impede
 * uma tela meio traduzida.
 *
 * A função se chama `txt` e não `t` porque `t` já é o tema em toda tela
 * (`const t = useTema()`).
 *
 * As palavras do desfecho (VALIDADO, JÁ VALIDADO…) ficam em
 * `src/dominio/tipos.ts`, que é idêntico ao do app nativo e não pode mudar.
 * A tradução delas entra aqui, pelo mesmo caminho dos outros textos.
 */

import {useEffect, useState} from 'react';

import {ESPANHOL} from './espanhol';

export type Idioma = 'pt' | 'es';

/** O mesmo nome que o index.html lê, antes de o bundle existir. */
const CHAVE = 'tmobi.validador.idioma';

function doServidor(): Idioma {
  // `typeof` porque isto também é importado fora da janela: num worker não
  // existe `window`, e a referência crua derrubaria o módulo na carga.
  const config = typeof window !== 'undefined' ? window.TMOBI_CONFIG : undefined;
  return config?.idioma === 'es' ? 'es' : 'pt';
}

function doAparelho(): Idioma | null {
  try {
    const salvo = localStorage.getItem(CHAVE);
    return salvo === 'es' || salvo === 'pt' ? salvo : null;
  } catch {
    return null;
  }
}

let atual: Idioma = doAparelho() ?? doServidor();
const ouvintes = new Set<() => void>();

export function idioma(): Idioma {
  return atual;
}

export function definirIdioma(novo: Idioma): void {
  if (novo === atual) {
    return;
  }
  atual = novo;
  try {
    localStorage.setItem(CHAVE, novo);
  } catch {
    // Sem armazenamento, a escolha vale só até recarregar.
  }
  ouvintes.forEach(avisar => avisar());
}

/**
 * O texto na língua da vez. `valores` preenche os espaços marcados com
 * chaves: `txt('Entrou às {hora}', {hora})`.
 */
export function txt(texto: string, valores?: Record<string, string | number>): string {
  const traduzido = atual === 'es' ? ESPANHOL[texto] ?? texto : texto;

  if (!valores) {
    return traduzido;
  }
  return traduzido.replace(/\{(\w+)\}/g, (inteiro, nome: string) =>
    nome in valores ? String(valores[nome]) : inteiro,
  );
}

/**
 * Texto que veio de `src/dominio/` — a palavra e a mensagem do desfecho.
 *
 * Existe separado do `txt` porque aqui a chave não pode ser escrita na
 * chamada: ela chega pronta em `resultado.palavra`, vinda do `DESFECHO`, que
 * é idêntico ao do app nativo e não pode mudar. Quem garante que as doze
 * palavras e as doze mensagens têm espanhol é `scripts/conferir-idioma.mjs`,
 * que lê o `DESFECHO` direto e cobra cada uma.
 */
export function txtDoDominio(texto: string): string {
  return txt(texto);
}

/** Redesenha a tela quando o operador troca o idioma. */
export function useIdioma(): Idioma {
  const [, setVersao] = useState(0);

  useEffect(() => {
    const ouvinte = () => setVersao(v => v + 1);
    ouvintes.add(ouvinte);
    return () => {
      ouvintes.delete(ouvinte);
    };
  }, []);

  return atual;
}

/** Para datas e horas: o formato do país, não o do aparelho. */
export function localeAtual(): string {
  return atual === 'es' ? 'es-PY' : 'pt-BR';
}
